// «Quando succede» con memo (0.1.40): le due select di spazio e progetto. «Tutti gli spazi» toglie
// il progetto; con uno spazio si sceglie «Tutti i progetti» o un progetto. Spazi e progetti li dà
// memo quando sa elencarli, altrimenti sono quelli che PopTown conosce; «Altro…» per scriverne uno.
import { useEffect, useState } from 'react';
import { Field, TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import { api } from '../../lib/api';
import type { MemoScopes, RoutineCondition } from '../../lib/api';

const ALL = '*';
const OTHER = '+';

export function MemoScopeFields({
  value,
  space,
  project,
  set,
}: {
  value: RoutineCondition;
  space: string;
  project: string;
  set: (patch: Partial<RoutineCondition>) => void;
}) {
  const [scopes, setScopes] = useState<MemoScopes | null>(null);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    api
      .memoScopes(value.connection)
      .then(setScopes)
      .catch(() => setScopes({ source: 'poptown', spaces: [] }));
  }, [value.connection]);

  const scope = value.scope ?? 'project';
  const spaces = scopes?.spaces ?? [];
  const known = spaces.find((s) => s.space === space);
  const freeSpace = typing || (scope !== 'all' && !!space && !known);
  const freeProject =
    freeSpace || (scope === 'project' && !!project && !known?.projects.includes(project));
  const spaceValue = scope === 'all' ? ALL : freeSpace ? OTHER : space;
  const projectValue = scope === 'space' ? ALL : freeProject ? OTHER : project;

  const pickSpace = (v: string) => {
    setTyping(v === OTHER);
    if (v === ALL) set({ scope: 'all', space: null, project: null, watch: '' });
    else if (v === OTHER) set({ scope: 'project', space: '', project: '', watch: '' });
    else set({ scope: 'space', space: v, project: null, watch: '' });
  };
  const pickProject = (v: string) => {
    if (v === ALL) set({ scope: 'space', project: null, watch: '' });
    else if (v === OTHER) set({ scope: 'project', project: '', watch: '' });
    else set({ scope: 'project', space, project: v, watch: '' });
  };
  const hint =
    scopes?.source === 'memo'
      ? 'Gli spazi che la connessione memo vede.'
      : 'Quelli che PopTown conosce (Integrazioni e progetti); «Altro…» per scriverne uno.';

  return (
    <>
      <div className="rw-grid">
        <Field label="Spazio memo" hint={hint}>
          <Select
            label="Spazio memo"
            value={spaceValue}
            onChange={pickSpace}
            options={[
              { value: ALL, label: 'Tutti gli spazi' },
              ...spaces.map((s) => ({ value: s.space, label: s.space })),
              { value: OTHER, label: 'Altro…' },
            ]}
            width="100%"
          />
        </Field>
        {scope !== 'all' && !freeSpace && (
          <Field label="Progetto memo" hint="«Tutti i progetti» guarda l’intero spazio.">
            <Select
              label="Progetto memo"
              value={projectValue}
              onChange={pickProject}
              options={[
                { value: ALL, label: 'Tutti i progetti' },
                ...(known?.projects ?? []).map((p) => ({ value: p, label: p })),
                { value: OTHER, label: 'Altro…' },
              ]}
              width="100%"
            />
          </Field>
        )}
      </div>
      {(freeSpace || (scope === 'project' && freeProject)) && (
        <div className="rw-grid">
          {freeSpace && (
            <Field label="Spazio" hint="Come in memo, es. Labs.">
              <TextField
                value={space}
                onChange={(v) => set({ scope: 'project', space: v, watch: '' })}
              />
            </Field>
          )}
          <Field label="Progetto" hint="PopTown lo prova su memo prima di salvare.">
            <TextField
              value={project}
              onChange={(v) => set({ scope: 'project', space, project: v, watch: '' })}
            />
          </Field>
        </div>
      )}
      {scope !== 'project' && (
        <span className="t-caption dim">
          A ogni controllo PopTown legge le anomalie di{' '}
          {scope === 'all' ? 'tutti gli spazi' : `tutto ${space}`} e prende la più vecchia, ovunque
          sia.
        </span>
      )}
    </>
  );
}
